import {
  IPageableRechargeSubscriptionResponse,
  IRechargeSubscription,
  IRechargeSubscriptionCreateParams,
  IRechargeSubscriptionGetParams,
  IRechargeSubscriptionUpdateParams,
  IRechargeSubscriptionUpdateQueryParams,
  ISingleRechargeSubscriptionResponse,
} from "@recharge";
import { Logger } from "@nestjs/common";
import axios from "axios";

import { RechargeResourceEndpoint } from "../../RechargeEndpoint";
import { RechargeAccessTokenManager } from "../RechargeAccessTokenManager";

export class RechargeSubscriptionsClient extends RechargeResourceEndpoint<
ISingleRechargeSubscriptionResponse,
IPageableRechargeSubscriptionResponse,
IRechargeSubscriptionGetParams,
IRechargeSubscription
> {

  public constructor(
    rechargeKeyManager: RechargeAccessTokenManager,
    baseUrl = "https://api.rechargeapps.com",
  ) {
    super(
      axios.create({ baseURL: baseUrl }),
      rechargeKeyManager.getNextAccessToken.bind(rechargeKeyManager),
      "/subscriptions",
      new Logger(RechargeSubscriptionsClient.name),
    );
  }

  protected override singleGetter(t: ISingleRechargeSubscriptionResponse): IRechargeSubscription {
    return t.subscription;
  }

  protected override multiGetter(t: IPageableRechargeSubscriptionResponse): IRechargeSubscription[] {
    return t.subscriptions;
  }

  /**
   * Updates an existing subscription with the specified parameters.
   *
   * @param rechargeSubscriptionId - The unique identifier of the subscription to update.
   * @param bodyParams - The parameters for updating the subscription.
   * @param bodyParams.charge_interval_frequency - The number of units (specified in order_interval_unit)
   * between each charge.
   * Required when updating order_interval_unit.
   * Max: 1000.
   * WARNING: This update will remove skipped and manually changed charges.
   * @param bodyParams.expire_after_specific_number_of_charges - The number of charges until the subscription expires.
   * @param bodyParams.external_variant_id - Unique number identifier of the product variant in your external platform.
   * @param bodyParams.external_variant_id.ecommerce - The variant id as it appears in the external e-commerce platform.
   * @param bodyParams.order_day_of_month - The day of the month the order is created on.
   *  WARNING: this update will remove skips and manually changed charges.
   * @param bodyParams.order_day_of_week - The day of the week the order is created on.
   *  WARNING: this update will remove skips and manually changed charges.
   * @param bodyParams.order_interval_frequency - The number of units (specified in order_interval_unit)
   * between each order. Required when updating order_interval_unit.
   * WARNING: This update will remove skipped and manually changed charges.
   * @param bodyParams.order_interval_unit - The frequency unit used to determine when a subscription order is created.
   * Possible values: day, week, month. WARNING: This update will remove skipped and manually changed charges.
   * @param bodyParams.plan_id - A Recharge Plan id that provides interval information.
   * Automatically fills certain properties if not provided directly.
   * @param bodyParams.price - The price of the item before discounts, taxes, or shipping has been applied.
   * @param bodyParams.product_title - The name of the product.
   * @param bodyParams.properties - A list of key/value pairs representing properties of the subscription.
   * @param bodyParams.properties[].name - The name of the property.
   * @param bodyParams.properties[].value - The value of the property.
   * @param bodyParams.quantity - The number of items in the subscription.
   * @param bodyParams.sku - A unique identifier of the item in the fulfillment.
   * @param bodyParams.sku_override - Flag indicating whether the SKU on
   * the subscription will be used to generate charges and orders.
   * @param bodyParams.use_external_variant_defaults - Flag instructing to pull the price from
   * the product variant passed.
   * @param bodyParams.variant_title - The name of the product variant.
   * @param bodyParams.status - The status of the subscription. Possible values: active, cancelled, expired.
   * Default is set to active.
   * @param bodyParams.cancellation_reason - Reason for subscription cancellation. (Required)
   * @param bodyParams.cancellation_reason_comments - Cancellation reason comment. Maximum length is 1024 characters.
   * @param queryParams - The query parameters for updating the subscription.
   * @param queryParams.commit - Controls whether the QUEUED charges linked to
   * the subscription should be regenerated upon subscription update. Default is false.
   * @param queryParams.force_update - If set to True, updates will also be applied to CANCELLED subscriptions.
   * If null or False, only ACTIVE subscriptions will be updated.
   * @returns The updated subscription.
   */
  public async updateSubscription(
    rechargeSubscriptionId: number,
    bodyParams: IRechargeSubscriptionUpdateParams,
    queryParams?: IRechargeSubscriptionUpdateQueryParams,
  ): Promise<IRechargeSubscription> {
    const result = await this.requestWithRetry<ISingleRechargeSubscriptionResponse, IRechargeSubscriptionUpdateParams>({
      url: `${this.resourcePath}/${rechargeSubscriptionId}`,
      config: {
        headers: this.getHeaders(this.accessTokenGetter()),
        method: "PUT",
        data: bodyParams,
        params: queryParams,
      },
      newKeyOnRetry: true,
      retryGatewayTimeout: true,
      retryConflict: false,
      truncateLongBody: false,
    });

    return result.data.subscription;
  }

  /**
   * Creates a new subscription with the specified parameters.
   *
   * @param bodyParams - The parameters for creating the subscription.
   * @param bodyParams.address_id - Unique numeric identifier of the customer’s address. (Required)
   * @param bodyParams.charge_interval_frequency - The number of units, specified in order_interval_unit,
   * between each charge. (Required)
   * @param bodyParams.expire_after_specific_number_of_charges - The number of charges until the subscription expires.
   * @param bodyParams.next_charge_scheduled_at - This will set the first charge date of a new subscription. (Required)
   * @param bodyParams.order_day_of_month - This is populated when order_interval_unit has value “month”.
   * Default value is 0.
   * @param bodyParams.order_day_of_week - This is populated when order_interval_unit has value “week”.
   * @param bodyParams.order_interval_frequency - The number of units, specified in order_interval_unit,
   * between each order. (Required)
   * @param bodyParams.order_interval_unit - The frequency unit used to determine when a subscription order is created.
   * Possible values: day, week, month. (Required)
   * @param bodyParams.plan_id - A Recharge Plan id that provides interval information. Automatically fills certain
   * properties if not provided directly.
   * @param bodyParams.price - The price of the product.
   * @param bodyParams.product_title - The name of the product.
   * @param bodyParams.properties - A list of key/value pairs representing properties of the subscription.
   * @param bodyParams.properties[].name - The name of the property.
   * @param bodyParams.properties[].value - The value of the property.
   * @param bodyParams.quantity - The quantity of the product. (Required)
   * @param bodyParams.external_product_id - Unique numeric identifier of the product id.
   * @param bodyParams.external_product_id.ecommerce - The product id as it appears in the external e-commerce platform.
   * @param bodyParams.external_variant_id - Unique numeric identifier of the product variant id. (Required)
   * @param bodyParams.external_variant_id.ecommerce - The variant id as it appears in the external e-commerce platform.
   * @param bodyParams.status - The status of the subscription. Possible values: active, cancelled, expired.
   * @param queryParams - The query parameters for updating the subscription.
   * @param queryParams.commit - Controls whether the QUEUED charges linked to
   * the subscription should be regenerated upon subscription update. Default is false.
   * Default is set to active.
   * @returns A promise that resolves to the newly created subscription.
   */
  public async createSubscription(
    bodyParams: IRechargeSubscriptionCreateParams,
    queryParams?: Pick<IRechargeSubscriptionUpdateQueryParams, "commit">,
  ): Promise<IRechargeSubscription> {
    const result =
      await this.requestWithRetry<ISingleRechargeSubscriptionResponse, IRechargeSubscriptionCreateParams>({
        url: this.resourcePath,
        config: {
          headers: this.getHeaders(this.accessTokenGetter()),
          method: "POST",
          data: bodyParams,
          params: queryParams,
        },
        newKeyOnRetry: true,
        retryGatewayTimeout: false,
        retryConflict: false,
        truncateLongBody: false,
      });

    return result.data.subscription;
  }

  /**
   * @param rechargeSubscriptionId the ID of the subscription to delete
   * @param send_email this value determines if the email should be sent for the specified subscription cancellation.
   * @return empty object
   * */
  public async deleteSubscription(
    rechargeSubscriptionId: number,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    { send_email }: { send_email: boolean },
  ): Promise<string> {

    const result = await this.requestWithRetry<string, { send_email: boolean }>({
      url: `${this.resourcePath}/${rechargeSubscriptionId}`,
      config: {
        headers: this.getHeaders(this.accessTokenGetter()),
        method: "DELETE",
        data: {
          send_email,
        },
      },
      newKeyOnRetry: true,
      retryGatewayTimeout: true,
      retryConflict: true,
      truncateLongBody: false,
    });

    return result.data;
  }

}
